import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Link2, Link2Off, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { FacebookIcon } from "~/components/icons/facebook-icon";
import { LinkedInIcon } from "~/components/icons/linkedin-icon";
import { XIcon } from "~/components/icons/x-icon";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import type { KeyManager } from "~/lib/crypto";
import {
  generateGoalShareSlugFn,
  removeGoalShareSlugFn,
} from "~/lib/functions/goal-of-life";
import { goalOfLifeQueryKeys } from "~/lib/queries/goalOfLifeQuery";
import { milestonesByGoalQueryOptions } from "~/lib/queries/milestoneQuery";

interface ShareGoalDialogProps {
  goalId: string;
  goalTitle: string;
  goalDescription: string | null;
  categoryName: string;
  shareSlug: string | null;
  /** Decrypts the milestones, whose titles the link publishes with the goal. */
  keyManager: KeyManager | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareGoalDialog({
  goalId,
  goalTitle,
  goalDescription,
  categoryName,
  shareSlug: initialShareSlug,
  keyManager,
  open,
  onOpenChange,
}: ShareGoalDialogProps) {
  const queryClient = useQueryClient();
  // The dialog mounts fresh for each goal, so the slug it opens with is the
  // goal's; after that, only this dialog's create and stop change it.
  const [shareSlug, setShareSlug] = useState<string | null>(initialShareSlug);

  const shareUrl = shareSlug
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/goals/${shareSlug}`
    : null;

  const generateShareLinkMutation = useMutation({
    mutationFn: async () => {
      const milestones = await queryClient.fetchQuery(
        milestonesByGoalQueryOptions({ goalId, viewer: "owner", keyManager }),
      );
      return generateGoalShareSlugFn({
        data: {
          goalId,
          sharePlaintext: {
            title: goalTitle,
            description: goalDescription,
            categoryName,
            // A title this browser could not decrypt is not published.
            steps: milestones
              .filter((m) => m.isReadable)
              .map(({ id, title }) => ({ id, title })),
          },
        },
      });
    },
    onSuccess: (data) => {
      setShareSlug(data.shareSlug);
      queryClient.invalidateQueries({ queryKey: goalOfLifeQueryKeys.all });
    },
    onError: () => {
      toast.error("The link was not created. Try again.");
    },
  });

  const removeShareLinkMutation = useMutation({
    mutationFn: () => removeGoalShareSlugFn({ data: { goalId } }),
    onSuccess: () => {
      setShareSlug(null);
      queryClient.invalidateQueries({ queryKey: goalOfLifeQueryKeys.all });
      toast.success("The public link is deleted");
    },
    onError: () => {
      toast.error("The link was not deleted. Try again.");
    },
  });

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleShareSocial = (platform: string) => {
    if (!shareUrl) return;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(`Check out my goal: ${goalTitle}`);

    let socialUrl = "";
    switch (platform) {
      case "facebook":
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case "twitter":
        socialUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case "linkedin":
        socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case "telegram":
        socialUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`;
        break;
    }

    if (socialUrl) {
      window.open(socialUrl, "_blank", "width=600,height=400");
    }
  };

  const isSharing = !!shareSlug;

  const socialButtons = [
    { platform: "facebook", label: "Share on Facebook", Icon: FacebookIcon },
    { platform: "linkedin", label: "Share on LinkedIn", Icon: LinkedInIcon },
    { platform: "telegram", label: "Share on Telegram", Icon: Send },
    { platform: "twitter", label: "Share on X", Icon: XIcon },
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {!isSharing ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                Share a public link to this goal?
              </DialogTitle>
              <DialogDescription>
                Anyone with the link can see, without signing in:
              </DialogDescription>
            </DialogHeader>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-900">
              <li>the goal, its description and its category</li>
              <li>its steps and which are done</li>
              <li>your name and profile photo</li>
            </ul>
            <p className="text-text-secondary text-sm">
              This makes an unencrypted copy of those words. Stop sharing at any
              time to delete it. Your other goals stay private.
            </p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => generateShareLinkMutation.mutate()}
                disabled={generateShareLinkMutation.isPending}
              >
                <Link2 aria-hidden />
                {generateShareLinkMutation.isPending
                  ? "Creating link…"
                  : "Create link"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                Public link
              </DialogTitle>
              <DialogDescription>
                Anyone with this link can see the goal, its steps, and your name
                and photo.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <Input
                value={shareUrl || ""}
                readOnly
                aria-label="Public link"
                className="mb-0 flex-1 bg-gray-50 text-gray-600"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Copy link"
                onClick={handleCopyLink}
                disabled={!shareUrl}
              >
                <Copy className="h-4 w-4 text-gray-600" aria-hidden />
              </Button>
            </div>

            <Card className="flex flex-row items-center justify-between rounded-xl p-4">
              <p className="text-sm font-medium text-gray-900">Or share on</p>
              <div className="flex justify-center gap-4">
                {socialButtons.map(({ platform, label, Icon }) => (
                  <Button
                    key={platform}
                    variant="ghost"
                    size="icon"
                    aria-label={label}
                    className="h-9 w-9 rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-600"
                    onClick={() => handleShareSocial(platform)}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>
                ))}
              </div>
            </Card>

            <Button
              variant="ghost"
              className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => removeShareLinkMutation.mutate()}
              disabled={removeShareLinkMutation.isPending}
            >
              <Link2Off aria-hidden />
              {removeShareLinkMutation.isPending ? "Stopping…" : "Stop sharing"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
